import { useEffect, useState } from 'react';
import { updateConsultation } from '../../../../api/doctor';
import { IntakeTextarea } from '../../../nurse/components/IntakeField';
import { nurse as c } from '../../../nurse/styles/nurseClasses';
import DoctorDentalChart from '../../../doctor/components/DoctorDentalChart';
import {
  EXTRA_ORAL_FIELDS,
  INTRA_ORAL_FIELDS,
  buildDentalExamPayload,
  dentalExamToForm,
  followUpToForm,
} from '../../../doctor/doctorDentalExamForm';
import { useToast } from '../../context/ToastContext';
import { fo } from '../../styles/frontOfficeModuleClasses';

const CURRENT_TEXTAREA =
  'w-full cursor-default resize-y rounded-lg border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm text-slate-500 opacity-80 shadow-sm';
const CURRENT_INPUT =
  'w-full cursor-default rounded-lg border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm text-slate-500 opacity-80 shadow-sm';
const NEW_TEXTAREA = c.textarea;
const NEW_INPUT = c.input;

function CurrentThenNew({
  id,
  label,
  currentValue,
  newValue,
  onNewChange,
  rows = 2,
  inputType = 'textarea',
  placeholder = '',
  hint = '',
}) {
  const hasCurrent = !(currentValue == null || String(currentValue).trim() === '');
  const displayCurrent = hasCurrent ? String(currentValue) : '';

  return (
    <div className="space-y-2 rounded-xl border border-slate-200/80 bg-white p-3">
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}

      <div>
        <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">
          Current (doctor)
        </p>
        {inputType === 'textarea' ? (
          <textarea
            id={`${id}-current`}
            className={CURRENT_TEXTAREA}
            rows={rows}
            value={displayCurrent}
            readOnly
            disabled
            placeholder="—"
          />
        ) : (
          <input
            id={`${id}-current`}
            type={inputType}
            className={CURRENT_INPUT}
            value={displayCurrent}
            readOnly
            disabled
            placeholder="—"
          />
        )}
        {!hasCurrent ? (
          <p className="mt-1 text-xs italic text-slate-400">Nothing recorded</p>
        ) : null}
      </div>

      <div>
        <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-wide text-teal-700">
          New (correction)
        </p>
        {inputType === 'textarea' ? (
          <textarea
            id={`${id}-new`}
            className={NEW_TEXTAREA}
            rows={rows}
            value={newValue}
            onChange={(e) => onNewChange(e.target.value)}
            placeholder={placeholder || 'Enter corrected value…'}
          />
        ) : (
          <input
            id={`${id}-new`}
            type={inputType}
            className={NEW_INPUT}
            value={newValue}
            onChange={(e) => onNewChange(e.target.value)}
            placeholder={placeholder || 'Enter corrected value…'}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Front office correction of doctor notes — same layout as the doctor form.
 * Current values are greyed read-only; new values sit directly underneath.
 */
export default function EditConsultationRecordModal({ consultation, onClose, onSaved }) {
  const { showToast } = useToast();
  const currentDental = dentalExamToForm(consultation?.dental_exam);
  const currentFollowUp = followUpToForm(consultation?.dental_exam?.follow_up);

  const [diagnosis, setDiagnosis] = useState(consultation?.diagnosis || '');
  const [clinicalNotes, setClinicalNotes] = useState(consultation?.notes || '');
  const [dental, setDental] = useState(currentDental);
  const [followUp, setFollowUp] = useState(currentFollowUp);
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const exam = dentalExamToForm(consultation?.dental_exam);
    const fu = followUpToForm(consultation?.dental_exam?.follow_up);
    setDiagnosis(consultation?.diagnosis || '');
    setClinicalNotes(consultation?.notes || '');
    setDental(exam);
    setFollowUp(fu);
    setEditReason('');
  }, [consultation]);

  if (!consultation) return null;

  function setExtra(key, value) {
    setDental((prev) => ({
      ...prev,
      extra_oral: { ...prev.extra_oral, [key]: value },
    }));
  }

  function setIntra(key, value) {
    setDental((prev) => ({
      ...prev,
      intra_oral: { ...prev.intra_oral, [key]: value },
    }));
  }

  function setInvestigation(patch) {
    setDental((prev) => ({
      ...prev,
      investigations: {
        ...(prev.investigations || {}),
        ...patch,
      },
    }));
  }

  function setDentalCharting(charting) {
    setDental((prev) => ({
      ...prev,
      dental_charting: charting,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const reason = editReason.trim();
    if (!reason) {
      showToast('Please explain why you are correcting this record', 'error');
      return;
    }

    setSaving(true);
    try {
      const dental_exam = buildDentalExamPayload(dental, followUp);
      // Always persist charting from the correction form (including cleared teeth).
      dental_exam.dental_charting = dental.dental_charting;

      const original = consultation.dental_exam && typeof consultation.dental_exam === 'object'
        ? consultation.dental_exam
        : {};
      const previousFollowUp = original.follow_up || null;
      const newDate = String(followUp?.date || '').trim();

      // Follow-up date is optional on corrections. Only schedule/update when a date is entered.
      if (newDate) {
        const history = Array.isArray(previousFollowUp?.history)
          ? [...previousFollowUp.history]
          : [];
        const dateChanged = previousFollowUp
          && (
            String(previousFollowUp.date || '') !== newDate
            || String(previousFollowUp.time || '') !== String(followUp.time || '').trim()
          );
        if (dateChanged) {
          history.push({
            action: 'corrected',
            at: new Date().toISOString(),
            by_role: 'front_office',
            from_date: previousFollowUp.date || null,
            from_time: previousFollowUp.time || null,
            to_date: newDate,
            to_time: String(followUp.time || '').trim() || null,
            reason: reason,
          });
        }
        dental_exam.follow_up = {
          date: newDate,
          time: String(followUp.time || '').trim() || null,
          notes: String(followUp.notes || '').trim() || null,
          status: 'scheduled',
          ...(history.length ? { history } : {}),
        };
      } else if (previousFollowUp) {
        // Keep the existing scheduled follow-up unchanged when no new date is provided.
        dental_exam.follow_up = previousFollowUp;
      } else {
        delete dental_exam.follow_up;
      }

      await updateConsultation(consultation.id, {
        diagnosis: diagnosis.trim() || null,
        notes: clinicalNotes.trim() || null,
        actions_taken: consultation.actions_taken ?? null,
        dental_exam,
        edit_reason: reason,
      });
      showToast('Medical record corrected. Previous and new values are on file.', 'success');
      await onSaved?.();
      onClose?.();
    } catch (err) {
      showToast(err.message || 'Failed to update medical record', 'error');
    } finally {
      setSaving(false);
    }
  }

  const currentInv = currentDental.investigations || {};
  const newInv = dental.investigations || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-consultation-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget && !saving) onClose?.();
      }}
    >
      <form
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-xl sm:p-6"
        onSubmit={handleSubmit}
      >
        <div className="mb-4">
          <h2 id="edit-consultation-title" className={c.sectionTitle}>
            Correct doctor notes
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Same sections the doctor filled in. Grey fields are the current record (read only).
            Enter the corrected values in the fields underneath.
          </p>
        </div>

        <div className="space-y-4">
          <section className={c.sectionPanel} aria-labelledby="corr-diagnosis-heading">
            <h3 id="corr-diagnosis-heading" className={c.sectionTitle}>
              Diagnosis / clinical impression
            </h3>
            <div className="mt-4 space-y-3">
              <CurrentThenNew
                id="corr-diagnosis"
                label="Diagnosis"
                currentValue={consultation.diagnosis}
                newValue={diagnosis}
                onNewChange={setDiagnosis}
                inputType="textarea"
                rows={3}
                placeholder="Enter diagnosis or clinical impression…"
              />
              <CurrentThenNew
                id="corr-clinical-notes"
                label="Clinical notes and treatment plan"
                currentValue={consultation.notes}
                newValue={clinicalNotes}
                onNewChange={setClinicalNotes}
                inputType="textarea"
                rows={4}
                placeholder="Detail the treatment plan, counseling provided, and clinical reasoning…"
              />
            </div>
          </section>

          <section className={c.sectionPanel} aria-labelledby="corr-extra-oral-heading">
            <h3 id="corr-extra-oral-heading" className={c.sectionTitle}>
              Extra-oral exam
            </h3>
            <div className="mt-4 space-y-3">
              {EXTRA_ORAL_FIELDS.map((field) => (
                <CurrentThenNew
                  key={field.key}
                  id={`corr-extra-${field.key}`}
                  label={field.label}
                  hint={field.hint}
                  currentValue={currentDental.extra_oral?.[field.key]}
                  newValue={dental.extra_oral?.[field.key] || ''}
                  onNewChange={(v) => setExtra(field.key, v)}
                  inputType="textarea"
                  rows={2}
                  placeholder={field.hint}
                />
              ))}
            </div>
          </section>

          <section className={c.sectionPanel} aria-labelledby="corr-intra-oral-heading">
            <h3 id="corr-intra-oral-heading" className={c.sectionTitle}>
              Intra-oral exam
            </h3>
            <div className="mt-4 space-y-3">
              {INTRA_ORAL_FIELDS.map((field) => (
                <CurrentThenNew
                  key={field.key}
                  id={`corr-intra-${field.key}`}
                  label={field.label}
                  hint={field.hint}
                  currentValue={currentDental.intra_oral?.[field.key]}
                  newValue={dental.intra_oral?.[field.key] || ''}
                  onNewChange={(v) => setIntra(field.key, v)}
                  inputType="textarea"
                  rows={2}
                  placeholder={field.hint}
                />
              ))}
            </div>
          </section>

          <section className={c.sectionPanel} aria-labelledby="corr-dental-chart-heading">
            <h3 id="corr-dental-chart-heading" className={c.sectionTitle}>
              Digital dental chart
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Pre-treatment can be corrected below. Completed treatment stays as the doctor recorded it.
            </p>
            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-slate-200/80 bg-white p-3">
                <p className="mb-2 text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">
                  Current (doctor)
                </p>
                <div className="pointer-events-none opacity-80">
                  <DoctorDentalChart
                    value={currentDental.dental_charting}
                    readOnly
                  />
                </div>
              </div>
              <div className="rounded-xl border border-teal-200/80 bg-white p-3">
                <p className="mb-2 text-[0.65rem] font-bold uppercase tracking-wide text-teal-700">
                  New (correction) — Pre-treatment editable
                </p>
                <DoctorDentalChart
                  value={dental.dental_charting}
                  onChange={setDentalCharting}
                  editablePhases={['pre_treatment']}
                />
              </div>
            </div>
          </section>

          <section className={c.sectionPanel} aria-labelledby="corr-investigations-heading">
            <h3 id="corr-investigations-heading" className={c.sectionTitle}>
              Investigation and results
            </h3>
            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-slate-200/80 bg-white p-3">
                <p className="text-sm font-semibold text-slate-800">X-Ray results</p>
                <p className="mt-1 text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">
                  Current (doctor)
                </p>
                <p className="mt-1 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-500 opacity-80">
                  {currentInv.xray_performed
                    ? (currentInv.xray_results || 'Performed — no findings text')
                    : 'Not performed'}
                </p>
                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-600/30"
                    checked={Boolean(newInv.xray_performed)}
                    onChange={(e) =>
                      setInvestigation({
                        xray_performed: e.target.checked,
                        xray_results: e.target.checked ? newInv.xray_results || '' : '',
                      })
                    }
                  />
                  New — X-Ray performed
                </label>
                {newInv.xray_performed ? (
                  <div className="mt-2">
                    <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-wide text-teal-700">
                      New (correction)
                    </p>
                    <IntakeTextarea
                      id="corr-xray-results-new"
                      label="X-Ray findings / description"
                      required={false}
                      showRequiredMark={false}
                      className={c.textarea}
                      rows={3}
                      maxLength={700}
                      placeholder="Describe X-Ray findings…"
                      value={newInv.xray_results || ''}
                      onChange={(e) =>
                        setInvestigation({ xray_results: e.target.value.slice(0, 700) })
                      }
                    />
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-slate-200/80 bg-white p-3">
                <p className="text-sm font-semibold text-slate-800">Blood test results</p>
                <p className="mt-1 text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">
                  Current (doctor)
                </p>
                <p className="mt-1 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-500 opacity-80">
                  {currentInv.blood_test_performed
                    ? (currentInv.blood_test_results || 'Performed — no findings text')
                    : 'Not performed'}
                </p>
                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-600/30"
                    checked={Boolean(newInv.blood_test_performed)}
                    onChange={(e) =>
                      setInvestigation({
                        blood_test_performed: e.target.checked,
                        blood_test_results: e.target.checked
                          ? newInv.blood_test_results || ''
                          : '',
                      })
                    }
                  />
                  New — Blood test performed
                </label>
                {newInv.blood_test_performed ? (
                  <div className="mt-2">
                    <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-wide text-teal-700">
                      New (correction)
                    </p>
                    <IntakeTextarea
                      id="corr-blood-results-new"
                      label="Blood test findings / description"
                      required={false}
                      showRequiredMark={false}
                      className={c.textarea}
                      rows={3}
                      maxLength={700}
                      placeholder="Describe blood test findings…"
                      value={newInv.blood_test_results || ''}
                      onChange={(e) =>
                        setInvestigation({ blood_test_results: e.target.value.slice(0, 700) })
                      }
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <section className={c.sectionPanel} aria-labelledby="corr-follow-up-heading">
            <h3 id="corr-follow-up-heading" className={c.sectionTitle}>
              Follow-up
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Optional for corrections. Leave the date blank to keep the existing follow-up unchanged.
            </p>
            <div className="mt-4 space-y-3">
              <div className={`${c.vitalsGrid} gap-3`}>
                <CurrentThenNew
                  id="corr-follow-up-date"
                  label="Follow-up date"
                  currentValue={currentFollowUp.date}
                  newValue={followUp.date}
                  onNewChange={(v) => setFollowUp((prev) => ({ ...prev, date: v }))}
                  inputType="date"
                />
                <CurrentThenNew
                  id="corr-follow-up-time"
                  label="Follow-up time"
                  currentValue={currentFollowUp.time}
                  newValue={followUp.time}
                  onNewChange={(v) => setFollowUp((prev) => ({ ...prev, time: v }))}
                  inputType="time"
                />
              </div>
              <CurrentThenNew
                id="corr-follow-up-notes"
                label="Follow-up notes"
                currentValue={currentFollowUp.notes}
                newValue={followUp.notes}
                onNewChange={(v) => setFollowUp((prev) => ({ ...prev, notes: v }))}
                inputType="textarea"
                rows={3}
                placeholder="Reason for follow-up, instructions for the patient…"
              />
            </div>
          </section>

          <section className={c.sectionPanel}>
            <IntakeTextarea
              id="corr-edit-reason"
              label="Reason for correction"
              required
              showRequiredMark
              className={c.textarea}
              rows={2}
              placeholder="e.g. Typo in diagnosis / wrong finding noted"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
            />
          </section>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
          <button type="button" className={fo.btnOutline} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={fo.btnPrimary} disabled={saving}>
            {saving ? 'Saving…' : 'Save correction'}
          </button>
        </div>
      </form>
    </div>
  );
}
